import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../app/hooks';
import { aprTrack } from '../util/aperioTracking';
import { additionalFeeNames } from '../constants/discountedFees';
import { isServiceNameKey } from '../util/serviceKeys';
import { carAnswered, selectConsumer } from './consumerSlice';
import {
  consumerToFleetPath,
  fleetToConsumerPath,
  StepProgress,
} from './paths';
import {
  Skin, switchLinkAllowed, switchSkin, useSkin,
} from './skin';

/** A fee the booking adds by itself (mobile fee, travel charge ...). */
export const isFeeLine = (item: { nameKey?: string | null }): boolean => (
  isServiceNameKey(item.nameKey) && additionalFeeNames.includes(item.nameKey)
);

/** What the booking holds, for keeping the step across a switch. */
export function useStepProgress(): StepProgress {
  const consumer = useAppSelector(selectConsumer);
  const { serviceAddress, inService, dateTime } = useAppSelector(
    (s) => s.serviceLocation,
  );
  const carId = useAppSelector((s) => s.car.id);
  const services = useAppSelector((s) => s.serviceTypes.services);
  const addOns = useAppSelector((s) => s.addOns.addOns);
  return {
    hasAddress: !!serviceAddress?.postalCode && inService !== false,
    hasCar: !!carId && (carAnswered(consumer) || !consumer.carQuery),
    hasServices: services.length > 0 || addOns.some((a) => !isFeeLine(a)),
    hasTime: !!dateTime?.date,
  };
}

/**
 * The plain switch at the top of both skins (owner brief): "Booking for a
 * fleet? Use fleet booking." / "Booking one car? Use simple booking." It
 * keeps everything entered (one shared booking state) and lands on the
 * matching step. Renders nothing off the Aperio preset, and nothing for the
 * A/B's control arm (consumer/skin.ts SHOW_SWITCH_IN_CONTROL_ARM).
 */
export default function SkinSwitchLink() {
  const { t } = useTranslation();
  const skin = useSkin();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const progress = useStepProgress();
  if (!switchLinkAllowed()) return null;

  const target: Skin = skin === 'consumer' ? 'fleet' : 'consumer';
  const go = () => {
    aprTrack('skin_switch', { to: target });
    switchSkin(target);
    navigate(target === 'fleet'
      ? consumerToFleetPath(pathname, progress)
      : fleetToConsumerPath(pathname, progress));
  };

  return (
    <p className="apr-c-switch">
      {skin === 'consumer'
        ? t('consumer.switch.toFleetLead')
        : t('consumer.switch.toConsumerLead')}
      {' '}
      <button type="button" className="apr-c-switch__link" onClick={go}>
        {skin === 'consumer'
          ? t('consumer.switch.toFleet')
          : t('consumer.switch.toConsumer')}
      </button>
    </p>
  );
}
